import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useFieldCaptureProjects } from '@hooks/api/field-capture';
import { FieldCaptureStore } from '@models/fieldCapture.model';

interface LinkProjectModalProps {
  store: FieldCaptureStore | null;
  /** Super admin passes the selected org; org admins omit (own org context). */
  organizationId?: string;
  saving: boolean;
  onClose: () => void;
  onLink: (store: FieldCaptureStore, projectId: string | null) => void;
}

/**
 * Project picker for binding a field-capture store to a ZenHammer project.
 * Replaces the window.prompt flow: searchable list of the org's projects,
 * plus an unlink action when the store is already bound.
 */
const LinkProjectModal: React.FC<LinkProjectModalProps> = ({
  store,
  organizationId,
  saving,
  onClose,
  onLink,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const { data: projects = [], isLoading } = useFieldCaptureProjects(
    organizationId,
    !!store,
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return projects;
    return projects.filter((p) => p.name.toLowerCase().includes(needle));
  }, [projects, search]);

  const handleClose = () => {
    setSearch('');
    setSelected(null);
    onClose();
  };

  if (!store) return null;

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Link {store.storeName} ({store.externalKey}) to a project
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder="Search projects…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
        />
        {isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Loading projects…
          </Typography>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No projects match.
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {filtered.map((project) => (
              <ListItemButton
                key={project.projectId}
                selected={
                  selected === project.projectId ||
                  (!selected && store.projectId === project.projectId)
                }
                onClick={() => setSelected(project.projectId)}
              >
                <ListItemText
                  primary={project.name}
                  secondary={
                    store.projectId === project.projectId
                      ? 'Currently linked'
                      : undefined
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        {store.projectId ? (
          <Button
            color="inherit"
            disabled={saving}
            onClick={() => onLink(store, null)}
          >
            Unlink
          </Button>
        ) : null}
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selected || saving}
          onClick={() => selected && onLink(store, selected)}
        >
          {saving ? 'Linking…' : 'Link project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkProjectModal;
